import { prop } from '@ipfabric/utils/fp/prop';
import { useApiTableDeletionHelper } from 'components/ApiTable/helpers/useApiTableDeletionHelper';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import { ApiTablePageView } from 'components/ApiTablePage/ApiTablePageView';
import { useApiTablePage } from 'components/ApiTablePage/useApiTablePage';
import { Button } from 'components/Button/Button';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { useLocale } from 'containers/App/LocaleContext';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { EditLocalUserModal } from 'pages/Settings/Administration/LocalUsers/EditLocalUserModal';
import type { EditedUser } from 'pages/Settings/Administration/LocalUsers/LocalUserForm';
import { memo, useState } from 'react';
import type { FC } from 'react';
import { useApi } from 'utils/request/useApi';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';

export const usersApiEndpoint = '/tables/users';

const BareLocalUsersPage: FC = () => {
  const localUsersMsg = useLocale().localUsers;
  const confirmDialog = useSimpleConfirmDialog();
  const [editedUser, setEditedUser] = useState<EditedUser | 'new' | null>(null);
  const api = useApi();
  const globalCtxProps = useGlobalStateContextSelectors({
    currentUserId: prop('auth', 'id'),
  });
  const deletionHelper = useApiTableDeletionHelper({
    confirmDialog,
    confirmDialogMessages: localUsersMsg.confirmDeleteUser,
    getApiTableModel: () => localUsersApiTablePage.apiTableModel,
    deleteOne: (id: string) =>
      api.request({
        url: `/users/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
  });

  const endpoint = useTableEndpoint({
    apiEndpoint: usersApiEndpoint,
    alternative: (origEndpoint) => ({
      id: origEndpoint.id,
      columns: [
        '...',
        createFrontendOnlyColumn({
          key: 'action',
          disabledSort: true,
          title: localUsersMsg.actionColumnTitle,
          webDependencies: [
            // used to pass to edit form -> conform to EditedUser interface
            'id',
            'isLocal',
            'username',
            'timezone',
            'roleIds',
          ],
          render: (rowData: EditedUser) => (
            <div>
              <Button
                icon='fa-pen'
                onClick={() => {
                  setEditedUser(rowData);
                }}
                variant='tertiary'
                size='xxs'
                type='button'
              />
              {globalCtxProps.currentUserId !== rowData.id &&
                deletionHelper.renderDeleteButton(rowData.id)}
            </div>
          ),
        }),
      ],
    }),
  });

  const localUsersApiTablePage = useApiTablePage({
    endpoint,
    additionalApiTableProps: {
      loading: deletionHelper.loading,
      initialSort: { column: 'username', order: 'asc' },
    },
  });

  return (
    <div className='flex flex-col flex-1 gap p'>
      <div>
        <Button
          variant='secondary'
          type='button'
          onClick={() => {
            setEditedUser('new');
          }}
          icon='fa-plus'
          dataTestId='local-users-add-button'
        >
          {localUsersMsg.addButton}
        </Button>
      </div>
      <ApiTablePageView model={localUsersApiTablePage} />
      {confirmDialog.render()}
      {editedUser !== null && (
        <EditLocalUserModal
          editedUser={editedUser}
          onClose={() => {
            setEditedUser(null);
          }}
          onSaveSuccess={() => {
            localUsersApiTablePage.apiTableModel.dispatch(reloadTableData);
          }}
        />
      )}
    </div>
  );
};

export const LocalUsersPage = memo(BareLocalUsersPage);
